import { LitElement, css, html } from 'lit';
import { map } from 'lit-html/directives/map.js';

const URL_REG_EX = /^(\/produkte\/kategorie\/[^/]+)\/?$/;

export class FlensProductSliderCategories extends LitElement {
	static properties = {
		categories: { type: Array },
		currentCategory: { type: Number, attribute: 'current-category' },
	};

	static styles = css`
    :host {
      --button-padding: var(--spacing-000);

      block-size: var(--spacing-9);
      font-size: var(--font-size-0);
      margin-block: var(--spacing-000) var(--spacing-30);
      margin-inline-start: var(--spacing-2);
    }

    ul {
      block-size: 100%;
      display: flex;
      font-size: inherit;
      list-style: none;
      margin: 0;
      padding: 0;

      li {
        block-size: 100%;
        border: none;
        color: var(--white);
        cursor: pointer;
        position: relative;
        text-align: center;
        transition: all 500ms ease-in-out;

        &.is-active button {
          background-color: var(--blue-500);
          color: var(--white);
          cursor: default;
          margin-block: calc(-0.25rem);
          padding-block: calc(0.25rem + var(--button-padding));
        }

        button {
          background-color: var(--blue-400);
          block-size: 100%;
          border: none;
          cursor: pointer;
          color: var(--blue-800);
          font-family: inherit;
          font-size: inherit;
          inline-size: 100%;
          inset-block-start: 0;
          inset-inline-start: 0;
          padding: var(--button-padding);
          transition: all 0.3s ease-out;

          &:hover {
            background-color: var(--blue-500);
            color: var(--white);
          }

          &:active {
            background-color: var(--blue-500);
            color: var(--white);
          }

          &:focus-visible {
            outline: 2px solid var(--blue-800);
            outline-offset: 0.25rem;
            position: relative;
            z-index: 2;
          }
        }
      }
    }

    @media screen and (width >= 576px) {
      :host {
        font-size: var(--font-size-2);
      }
    }

    @media screen and (width >= 992px) {
      :host {
        block-size: var(--spacing-6);
        font-size: var(--font-size-1);
      }
    }

    @media screen and (width >= 1200px) {
      :host {
        --button-padding: var(--spacing-1);

        block-size: var(--spacing-9);
        font-size: var(--font-size-1);
        margin-block-end: var(--spacing-9);
      }
    }
  `;

	constructor() {
		super();
		this.categories = [];
		this.currentCategory = -1;
		this._renderCategory = this._renderCategory.bind(this);
		this._onPopState = this._onPopState.bind(this);
	}

	connectedCallback() {
		super.connectedCallback();
		window.addEventListener('popstate', this._onPopState);
	}

	disconnectedCallback() {
		window.removeEventListener('popstate', this._onPopState);
		super.disconnectedCallback();
	}

	firstUpdated(_changedProperties) {
		super.firstUpdated(_changedProperties);
		const categoryId = this._getCategoryIdFromUrl();
		if (categoryId) {
			this._changeCategory(categoryId);
		}
	}

	_onPopState(event) {
		const { categoryId } = event.state ?? {};
		this._changeCategory(categoryId);
	}

	_getCategoryIdFromUrl() {
		const categoryUrl = URL_REG_EX.exec(window.location.pathname)?.[1];
		if (!categoryUrl) {
			return -1;
		}
		return this.categories.find(({ url }) => url === categoryUrl)?.id;
	}

	updated(changedProperties) {
		if (this.currentCategory < 0) {
			this.currentCategory = this.categories[0]?.id ?? -1;
		}
		super.updated(changedProperties);
	}

	_onCategoryClick(event) {
		const id = Number.parseInt(event.target.dataset.categoryId);
		const { name, url } = this.categories.find(
			(category) => category.id === id,
		);
		window.history.pushState({ categoryId: id }, name, url);
		this._changeCategory(id);
	}

	_changeCategory(categoryId) {
		if (this.currentCategory === categoryId) {
			return;
		}
		this.currentCategory = categoryId;
		// eslint-disable-next-line compat/compat -- no need to run in Node
		this.dispatchEvent(new CustomEvent('change', { detail: { categoryId } }));
	}

	_renderCategory(category) {
		const isCurrent = category.id === this.currentCategory;
		return html`
      <li
        class="${isCurrent ? 'is-active' : ''}"
        data-category="${category.id}"
        data-name="${category.name}"
        data-url="${category.url}"
        aria-selected="${isCurrent}"
      >
        <button
          @click="${this._onCategoryClick}"
          data-category-id="${category.id}"
        >
          ${category.name}
        </button>
      </li>
    `;
	}

	render() {
		return html`
      <nav aria-label="Produkt-Kategorien">
        <ul>
          ${map(this.categories, this._renderCategory)}
        </ul>
      </nav>
    `;
	}
}

customElements.define(
	'flens-product-slider-categories',
	FlensProductSliderCategories,
);
